// Whether a retracted document is still live. One rule, one place.
//
// The rule is `core/src/tombstone.rs` (#235): two fields, a boolean that is
// present immediately and a `serverTimestamp()` that reads back null from the
// writing device's own cache, and a document is active only when **neither**
// says otherwise. Issue #70 is the other direction — a phantom package consume
// that read as live and billed a family for a lesson already prepaid.
//
// ── An adapter, not a second implementation ────────────────────────────────
//
// The core takes the two **fields**, not a document, so one rule serves four
// collections whose documents have nothing else in common, and two scalars
// marshal far cheaper than a record over the thousands of entries a server
// fold may read (`core-ffi/src/lib.rs` beside the export). What is left here is
// knowing that the two fields are called `voided` and `voidedAt`, as
// `src/data/packageShape.js` knows its document's shape.
//
// **A projection, not carriage** — #219's distinction, in
// `src/data/docShape.js`'s header: the core reads these two values. Nor does
// this call `isoInstantOf`: the rule wants a `Timestamp` reduced to its
// truthiness, not to a time (below).
//
// `ledger.js`, `makeup.js`, `practice.js` and `expenses.js` re-export this
// rather than wrapping it, so there is one implementation.
import { coreSync } from './core.js';

// ── `voidedAt` is not always a string, and the conversion is the adapter's ──
//
// The core takes `Option<String>` because the **empty-string case is part of
// the rule** — a timestamp field cleared to `""` rather than removed does not
// tombstone on its own — and that belongs beside the rest of the rule rather
// than here.
//
// What arrives is whatever Firestore hands back: a `Timestamp`, a `Date` in
// the tests, `null` from the writing device's own cache before the server
// answers, or the field missing altogether. A bare `String` parameter given a
// `Date` is "memory access out of bounds" (#215).
//
// So a non-string is reduced to **its JavaScript truthiness**, as
// `!entry.voidedAt` reads it: a `Date` or a `Timestamp` is truthy and
// tombstones; `0`, `NaN` and `false` are falsy and do not. A string passes
// through untouched so the core can apply the empty-string rule to it. The two
// together agree with `!entry.voidedAt` on every input.
export function isActive(doc) {
  const { voided, voidedAt } = tombstoneOf(doc);
  return coreSync().isActive(voided, voidedAt);
}

// The two fields as a core record takes them (for make-up entries, practice
// sessions and a stored payment, which cross whole):
// `voided` as a boolean and `voidedAt` reduced as above.
export function tombstoneOf(doc) {
  const at = doc?.voidedAt;
  return {
    voided: Boolean(doc?.voided),
    voidedAt: typeof at === 'string' ? at : ((at && 'set') || ''),
  };
}
